import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Dismissible "Join the Community" banner.
 *
 * Visibility rules:
 *  1. Admin must have enabled it via Page Builder → Community.
 *  2. The current user account must be at least `banner_delay_days` old
 *     (earned-discovery — no nag on day 1).
 *  3. The user has not dismissed it on this device (per-user localStorage).
 *
 * The CTA always points to `/api/community/go`, which 302s to the right
 * Dayroom URL for the user's grade — so no per-user branching here.
 */
const CommunityBanner = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // localStorage key is per-user so dismissals don't bleed across accounts
  // on a shared device.
  const dismissKey = user?.id ? `community_banner_dismissed:${user.id}` : null;

  useEffect(() => {
    if (!user) return;
    if (dismissKey && localStorage.getItem(dismissKey) === '1') {
      setDismissed(true);
      return;
    }
    axios
      .get(`${API}/api/pages/community-settings`)
      .then(({ data }) => setSettings(data))
      .catch(() => {/* hide on failure */});
  }, [user, dismissKey]);

  const onDismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  if (dismissed || !settings || !settings.banner_enabled || !user) return null;

  // Account-age gate. We compare against the user's `created_at` (ISO).
  // If we can't parse it for any reason, fail open (no banner) — a banner
  // we can't reason about is worse than no banner.
  const createdAt = user.created_at ? new Date(user.created_at) : null;
  if (!createdAt || Number.isNaN(createdAt.getTime())) return null;
  const ageDays = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
  if (ageDays < (settings.banner_delay_days || 0)) return null;

  return (
    <div
      className="relative mb-6 rounded-lg border border-[#0A7EA4]/30 bg-gradient-to-r from-[#0A7EA4]/10 to-purple-100/40 p-4 shadow-sm"
      data-testid="community-banner"
    >
      {/* pr-8 reserves space on the right edge so the absolutely-positioned
          dismiss (X) button never overlaps the CTA, regardless of CTA
          label length or breakpoint. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pr-8">
        <div className="flex items-start gap-3">
          <div className="hidden sm:flex w-10 h-10 rounded-lg bg-white/70 items-center justify-center text-[#0A7EA4] shrink-0">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-gray-900">{settings.banner_title}</div>
            <div className="text-sm text-gray-600 mt-0.5">{settings.banner_body}</div>
          </div>
        </div>
        <a
          href={`${API}/api/community/go`}
          className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-[#0A7EA4] hover:bg-[#0A7EA4]/90 text-white text-sm font-medium whitespace-nowrap shrink-0"
          data-testid="community-banner-cta"
        >
          {settings.banner_cta_label}
        </a>
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss community banner"
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-white/60"
        data-testid="community-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default CommunityBanner;
